import React from 'react';
import '../styles/DataManagementPage.css';

/**
 * Reusable pagination navigation component.
 * Renders a nav block with Prev/Next, Page Size selector, current page input, and total info.
 *
 * Props:
 * - page: number (1-based)
 * - pageSize: number
 * - total: number (total entries)
 * - onPageChange: (page:number) => void
 * - onPageSizeChange: (size:number) => void
 * - allowedPageSizes?: number[] (default [10,20,50,100])
 * - disabled?: boolean
 * - position?: 'top' | 'bottom' (adds margins)
 */
function PaginationNav({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  allowedPageSizes = [10, 20, 50, 100],
  disabled = false,
  position,
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / Math.max(1, pageSize || 1)));
  const canPrev = page > 1 && !disabled;
  const canNext = page < totalPages && !disabled;

  const handleSizeChange = (e) => {
    const val = parseInt(e.target.value, 10);
    const next = allowedPageSizes.includes(val) ? val : allowedPageSizes[allowedPageSizes.length - 1];
    onPageSizeChange && onPageSizeChange(next);
    // Reset to first page by convention
    onPageChange && onPageChange(1);
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = total === 0 ? 0 : Math.min(page * pageSize, total);

  return (
    <nav
      className="pagination-controls"
      style={{
        marginTop: position === 'bottom' ? 16 : 0,
        marginBottom: position === 'top' ? 12 : 0,
      }}
    >
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn-secondary btn-sm"
          disabled={!canPrev}
          onClick={() => onPageChange && onPageChange(Math.max(1, page - 1))}
        >
          « Prev
        </button>
        <button
          className="btn btn-secondary btn-sm"
          disabled={!canNext}
          onClick={() => onPageChange && onPageChange(Math.min(totalPages, page + 1))}
        >
          Next »
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
        <div className="pagination-info">
          {totalPages === 1
            ? `Showing ${total === 0 ? 0 : 1} to ${total} entries`
            : `Showing ${showingFrom} - ${showingTo} of ${total} entries`}
          <span className="pagination-page-info" style={{ marginLeft: 12 }}>
            Page {Math.min(page, totalPages)} of {totalPages}
          </span>
        </div>

        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          Page size
          <select value={pageSize} disabled={disabled} onChange={handleSizeChange} style={{ padding: '2px 6px' }}>
            {allowedPageSizes.map((sz) => (
              <option key={sz} value={sz}>
                {sz}
              </option>
            ))}
          </select>
        </label>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12 }}>Page</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={Math.min(page, totalPages)}
            disabled={disabled || totalPages === 0}
            onChange={(e) => {
              const raw = parseInt(e.target.value || '1', 10);
              if (Number.isNaN(raw)) return;
              const clamped = Math.min(Math.max(1, raw), totalPages);
              onPageChange && onPageChange(clamped);
            }}
            style={{ width: 64 }}
          />
        </div>
      </div>
    </nav>
  );
}

export default PaginationNav;
